import React, { useState, useEffect } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatGs, MODALIDADES } from "../lib/format";
import { StatusBadge } from "./StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, Ban } from "lucide-react";
import { toast } from "sonner";

const STAFF = ["admin", "encargado", "encargado_comisionista"];
const MOD = Object.fromEntries(MODALIDADES.map((m) => [m.id, m.label]));
const MOTIVOS = [["duplicada", "Boleta duplicada"], ["error_info", "Error de información"], ["otro", "Otros (describir)"]];
const chipCls = "font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200";

function Row({ k, v }) {
  return <div className="flex justify-between gap-3 text-sm py-1 border-b border-slate-100"><span className="text-[#64748B]">{k}</span><b className="text-[#0A1F44] text-right">{v || "—"}</b></div>;
}

function AnularForm({ envio, onDone }) {
  const [motivo, setMotivo] = useState("duplicada");
  const [detalle, setDetalle] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/envios/${envio.id}/anular`, { motivo, detalle: detalle || null });
      toast.success(`Boleta ${envio.code} anulada`); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-3 space-y-2" data-testid="anular-form">
      <div className="text-xs font-semibold text-rose-700">Motivo de anulación</div>
      {MOTIVOS.map(([id, label]) => (
        <label key={id} className="flex items-center gap-2 text-sm"><input type="radio" name="motivo" checked={motivo === id} onChange={() => setMotivo(id)} data-testid={`anular-motivo-${id}`} /> {label}</label>
      ))}
      <textarea className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={2} placeholder={motivo === "otro" ? "Describí el motivo (obligatorio)" : "Detalle (opcional)"} value={detalle} onChange={(e) => setDetalle(e.target.value)} data-testid="anular-detalle" />
      <button onClick={submit} disabled={saving} data-testid="anular-confirmar-btn" className="w-full flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold py-2 disabled:opacity-60">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirmar anulación
      </button>
    </div>
  );
}

export function EnvioDetalleModal({ code, onClose, onChanged }) {
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [err, setErr] = useState("");
  const [anular, setAnular] = useState(false);
  useEffect(() => {
    setE(null); setErr(""); setAnular(false);
    if (code) api.get(`/envios/by-code/${encodeURIComponent(code)}`).then(({ data }) => setE(data)).catch((x) => setErr(apiErr(x)));
  }, [code]);
  const close = () => onClose();
  const isAdmin = user?.role === "admin";
  const puedeAnular = e && STAFF.includes(user?.role) && e.status !== "anulado" && (isAdmin || !["entregado", "dispuesto", "devuelto_origen", "reasignado", "devuelto"].includes(e.status));
  return (
    <Dialog open={!!code} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="envio-detalle-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Boleta {code}</DialogTitle>
          <DialogDescription>{e ? <StatusBadge status={e.status} /> : "Información de la boleta"}</DialogDescription>
        </DialogHeader>
        {!e && !err && <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {err && <div className="text-sm text-rose-600" data-testid="envio-detalle-error">{err}</div>}
        {e && (
          <div data-testid="envio-detalle-info">
            <Row k="Remitente" v={e.remitente_nombre} />
            <Row k="WhatsApp remitente" v={e.remitente_whatsapp} />
            <Row k="Destinatario" v={e.destinatario} />
            <Row k="WhatsApp destinatario" v={e.whatsapp} />
            <Row k="Ciudad" v={e.ciudad} />
            <Row k="Ruta" v={`${e.sucursal_origen || "—"} → ${e.sucursal_destino || "—"}`} />
            <Row k="Modalidad" v={MOD[e.modalidad] || e.modalidad} />
            <Row k="Valor producto" v={formatGs(e.valor_producto)} />
            <Row k="Flete" v={`${formatGs(e.costo_envio)}${e.flete_confirmado ? "" : " (sin confirmar)"}`} />
            <Row k="Cobro al destinatario" v={formatGs(e.cobro_destinatario)} />
            <Row k="Contenido" v={e.contenido} />
            <Row k="Notas" v={e.notas} />
            <Row k="Creada" v={e.created_at ? new Date(e.created_at).toLocaleString("es-PY") : ""} />
            {e.anulacion && <Row k="Anulación" v={`${e.anulacion.motivo_label}${e.anulacion.detalle ? ` · ${e.anulacion.detalle}` : ""}`} />}
            <div className="mt-3 text-xs font-semibold text-[#64748B]">Historial</div>
            <ul className="mt-1 space-y-1 max-h-40 overflow-y-auto">
              {(e.events || []).map((ev, i) => (
                <li key={i} className="text-xs text-[#0A1F44]"><b>{ev.label}</b> · {ev.at ? new Date(ev.at).toLocaleString("es-PY") : ""} · {ev.by}{ev.nota ? ` — ${ev.nota}` : ""}</li>
              ))}
            </ul>
            {puedeAnular && !anular && (
              <button onClick={() => setAnular(true)} data-testid="anular-boleta-btn" className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50 text-sm font-semibold py-2">
                <Ban className="h-4 w-4" /> Anular boleta
              </button>
            )}
            {anular && <AnularForm envio={e} onDone={() => { onChanged?.(); close(); }} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CodeChip({ code, className = chipCls, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(null);
  if (!STAFF.includes(user?.role)) return <span className={className}>{code}</span>;
  return (
    <>
      <button type="button" onClick={(ev) => { ev.stopPropagation(); setOpen(code); }} data-testid={`code-chip-${code}`}
        className={`${className} hover:bg-blue-50 hover:border-blue-300 hover:text-[#0A1F44] cursor-pointer transition-colors`}>{code}</button>
      <EnvioDetalleModal code={open} onClose={() => setOpen(null)} onChanged={onChanged} />
    </>
  );
}

export function CodeList({ codes, onChanged }) {
  if (!codes?.length) return <div className="text-sm text-[#64748B]">Sin envíos.</div>;
  return <div className="flex flex-wrap gap-1.5">{codes.map((c) => <CodeChip key={c} code={c} onChanged={onChanged} />)}</div>;
}
